/**
 * v1 sunset: hand the auth pages over to the new app.
 *
 * Retiring these pages drains v1 by attrition — no SSO handoff, nothing that can fail at
 * runtime. Know what it does and does not reach:
 *
 * v1 sessions are one day of INACTIVITY, not one day. `user.repo.ts` createSession sets
 * expires_at to now+1d, and getUserFromValidSession slides it forward to now+1d on any
 * request with under 23h left — every authenticated request, in practice. There is no
 * absolute cap and no reaper, so a user who opens v1 once a day holds one session for
 * months. (An earlier version of this comment claimed a flat one-day expiry and "65-80% of
 * the active base passes back through login every day". That is wrong.)
 *
 * So attrition only collects the lapsed: away 24h+, session dead, next visit goes to v2.
 * Daily-active users never expire, never see a 401, and stay on v1 until the hard redirect
 * moves them — which is what the launch banner's SSO handoff is for.
 *
 * Login is reached two ways and both end up here: typing/bookmarking /login, and the
 * expiry bounce — `data/api.ts` clears localStorage on a 401 and reloads, AuthGuard then
 * redirects to /login client-side, which the route guard in `routes.tsx` catches.
 *
 * v2 login absorbs the whole v1 population (v2 `handlers/auth/auth.go` Login):
 *   - password unchanged since the port      -> the synced bcrypt verifies
 *   - password reset on v1 after the port    -> stale v2 bcrypt; RefreshLegacyBcryptIfDifferent
 *                                               point-reads Aurora and retries verify once
 *   - no password on either side (~68% of ported users, the pre-020f205 NULL password_hash
 *     cohort)                                -> 409, v2 emails a one-tap set-password link
 *   - never ported into v2                   -> 401 with no way back once this is on. This is
 *                                               the one dead end, so check coverage before
 *                                               shipping (see docs/v1-login-sunset.md).
 *
 * reset-password stays 'off' deliberately: it is now the only path that writes a bcrypt into
 * Aurora, which is what the reactive refresh above reads. v2's own reset writes v2-side only.
 *
 * The search page is wired here too, for the Chrome extension's deep links. It is the one page
 * whose query string has to be rewritten on the way over (`v2SearchDeepLink.ts`) — v2 accepts
 * none of v1's parameter names. Both redirects cover it: the pre-React one catches extension
 * clicks (always a fresh page load) before AuthGuard can drop the query on the way to /login,
 * and the route guard in `routes.tsx` catches in-app navigation — the tracking tabs, and the
 * catch-all that sends `/` to the tracking tab. Search is the whole of v1 for most users, so
 * retiring it retires the app; billing, API key and the rest stay reachable by direct URL.
 */

import { v2SearchQueryFromV1 } from 'src/v2SearchDeepLink';

export const V2_BASE_URL = 'https://v2.tracktaco.com';

export type V2SunsetPage = 'register' | 'login' | 'resetPassword' | 'search';

/**
 * 'off'       — v1 still serves the page.
 * 'immediate' — redirect before anything renders. For pages nobody has an expectation
 *               about (a new signup does not care which app they land in).
 * 'announce'  — show the hand-off card first. For pages returning users know, where
 *               being thrown onto another domain unannounced reads as broken.
 */
export type V2SunsetMode = 'off' | 'immediate' | 'announce';

/** Which v1 pages are retired, and how. Changing a value here is the whole switch. */
export const V2_SUNSET: Record<V2SunsetPage, V2SunsetMode> = {
  register: 'immediate',
  login: 'announce',
  resetPassword: 'off', // still the only writer of an Aurora password_hash — see below
  // Retires the v1 app, not just a page. Shipped 2026-10-06, when a week of getuser logs
  // showed 6 people still using v1 in a browser.
  search: 'immediate',
};

// v2 mirrors v1's auth paths one-for-one (v2 `web/src/router.tsx`), so each page
// hands off to the same path on the new origin. The search page matches too.
const V2_PATH: Record<V2SunsetPage, string> = {
  register: '/register',
  login: '/login',
  resetPassword: '/reset-password',
  search: '/app/account/tracking',
};

// The v1 routes that serve each page, for the pre-render redirect below.
const V1_PATHS: Record<V2SunsetPage, string[]> = {
  register: ['/register', '/register-unprotected'],
  login: ['/login', '/login-unprotected'],
  resetPassword: ['/reset-password'],
  // `/app/account` renders the tracking tab by default; `tracking-old` is the "Simple" search.
  search: ['/app/account', '/app/account/tracking', '/app/account/tracking-old'],
};

/**
 * Pages whose query string means something different on v2. Everything else forwards its
 * query verbatim, which is right for auth pages (utm tags and nothing else) and wrong for
 * search, where v2 shares none of v1's parameter names.
 */
const TRANSLATE_QUERY: Partial<Record<V2SunsetPage, (search: string) => string>> = {
  search: v2SearchQueryFromV1,
};

export function sunsetMode(page: V2SunsetPage): V2SunsetMode {
  return V2_SUNSET[page];
}

/** The v2 URL for a page, carrying the query string over (translated where it has to be). */
export function v2UrlFor(page: V2SunsetPage): string {
  const translate = TRANSLATE_QUERY[page];
  const query = translate
    ? translate(window.location.search)
    : window.location.search.replace(/^\?/, '');
  return `${V2_BASE_URL}${V2_PATH[page]}${query ? `?${query}` : ''}`;
}

/**
 * Redirect an 'immediate' page before React mounts, so it never paints v1 chrome —
 * no card, no cookie banner, no flash. Returns true when it has taken over navigation.
 * Call this from `index.tsx` and skip rendering if it returns true.
 */
export function redirectIfRetiredImmediately(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const pages = Object.keys(V1_PATHS) as V2SunsetPage[];
  const page = pages.find((p) => V1_PATHS[p].indexOf(path) !== -1);

  if (!page || V2_SUNSET[page] !== 'immediate') return false;

  window.location.replace(v2UrlFor(page));
  return true;
}
